import { DateTime } from "luxon";
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgDialog,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { getStartedAtTs } from "@intelligems/ig-utils";
import { Alert, Grid } from "@mui/material";
import { useGetExperiencesList } from "@src/api/experiences-list/get-experiences-list";
import { useGetOrg } from "@src/api/organizations/get-organization";
import { useGetExperienceSchedules } from "@src/api/schedules/get-schedules-by-experience-id";
import { usePutExperienceSchedule } from "@src/api/schedules/put-experience-schedule";
import { SkeletonGroup } from "@src/components/Utils/SkeletonGroup";
import {
  isUsedInExperiment,
  isUsedInPersonalization,
} from "@src/features/personalizations/utils";
import { OFFSET } from "@src/features/scheduleExperience/constants";
import { EndSchedule } from "@src/features/scheduleExperience/schedules/EndSchedule";
import { PauseSchedule } from "@src/features/scheduleExperience/schedules/PauseSchedule";
import { StartSchedule } from "@src/features/scheduleExperience/schedules/StartSchedule";
import { ScheduleErrors } from "@src/features/scheduleExperience/types";
import {
  buildSavePayload,
  getEndedDisabledReason,
  getPausedDisabledReason,
  getStartDisabledReason,
  isConfirmDisabled,
} from "@src/features/scheduleExperience/utils";
import {
  getDialog,
  getDialogPropDetails,
  setDialog,
} from "@src/store/slices/global";
import { theme } from "@src/theme";

// Separated out from ScheduleExperienceDialog because this dialog is going to take in schedules from a get all
export const SchedulePersonalizationDialog = () => {
  const dispatch = useDispatch();
  const dialog = useSelector(getDialog("schedulePersonalization"));
  const dialogProps = useSelector(
    getDialogPropDetails("schedulePersonalization")
  );
  const { experience, schedules } = dialogProps || {};
  const organization = useGetOrg();
  const putTestSchedule = usePutExperienceSchedule();
  const [cancelSchedule, setCancelSchedule] = useImmer<boolean>(false);
  const experiencesList = useGetExperiencesList({
    category: "experiment",
  });
  const personalizationsList = useGetExperiencesList({
    category: "personalization",
  });

  const [startDateTime, setStartDateTime] = useImmer<DateTime | null>(null);
  const [pauseDateTime, setPauseDateTime] = useImmer<DateTime | null>(null);
  const [endDateTime, setEndDateTime] = useImmer<DateTime | null>(null);

  const [errors, setErrors] = useImmer<ScheduleErrors>({
    start: false,
    pause: false,
    end: false,
  });

  const handleClose = () => {
    dispatch(
      setDialog({
        dialog: "schedulePersonalization",
        open: false,
        props: undefined,
      })
    );
  };

  const { startAtSchedule, pauseAtSchedule, endAtSchedule } = useMemo(() => {
    if (experience?.status === "started") {
      setStartDateTime(
        DateTime.fromMillis(
          getStartedAtTs(experience.experienceActions)!.actionTs
        )
      );
    }

    const startAtSchedule =
      schedules.find((schedule) => schedule.action === "start") || null;

    if (startAtSchedule) {
      setStartDateTime(DateTime.fromMillis(startAtSchedule.executionTime));
    }

    const pauseAtSchedule =
      schedules.find((schedule) => schedule.action === "pause") || null;
    if (pauseAtSchedule) {
      setPauseDateTime(DateTime.fromMillis(pauseAtSchedule.executionTime));
    }
    const endAtSchedule =
      schedules.find((schedule) => schedule.action === "end") || null;
    if (endAtSchedule) {
      setEndDateTime(DateTime.fromMillis(endAtSchedule.executionTime));
    }
    return {
      startAtSchedule,
      pauseAtSchedule,
      endAtSchedule,
    };
  }, [schedules, experience?.id]);

  const timeNow = useMemo(
    () =>
      DateTime.now()
        .setZone(organization?.data?.org?.timezone ?? "UTC")
        .plus({ minutes: OFFSET }),
    [organization.isSuccess]
  );

  const isUsedInExperimentResult = useMemo(() => {
    if (
      experience?.category === "personalization" &&
      experiencesList.isSuccess
    ) {
      return isUsedInExperiment(
        experiencesList.data.experiencesList,
        experience?.id ?? null
      );
    }
    return { name: undefined, id: undefined };
  }, [experiencesList, experience]);

  const isUsedInPersonalizationResult = useMemo(() => {
    if (
      experience?.category === "experiment" &&
      personalizationsList.isSuccess
    ) {
      return isUsedInPersonalization(
        personalizationsList.data.experiencesList,
        experience["variations"] || []
      );
    }
    return [];
  }, [personalizationsList, experience]);

  const isUsed = isUsedInExperimentResult.id
    ? [isUsedInExperimentResult]
    : isUsedInPersonalizationResult;

  const onSaveClick = () => {
    putTestSchedule.mutate({
      body: buildSavePayload(
        experience?.id ?? "",
        cancelSchedule,
        startAtSchedule,
        startDateTime,
        pauseAtSchedule,
        pauseDateTime,
        endAtSchedule,
        endDateTime,
        experience.status
      ),
      queryParams: undefined,
      pathParams: undefined,
    });
  };

  useEffect(() => {
    if ((startAtSchedule && !startDateTime) || !startDateTime) {
      setEndDateTime(null);
      setPauseDateTime(null);
    }
  }, [startAtSchedule, startDateTime]);

  if (!experience) {
    return null;
  }

  return (
    <IgDialog
      showCloseIcon={true}
      hideActions={true}
      title={
        cancelSchedule ? (
          "Confirm Cancellation"
        ) : schedules.length > 0 ? (
          <Grid container direction={"column"} rowSpacing={4}>
            <Grid item>
              <IgTypography variant={"h2"} sx={{ fontWeight: 600 }}>
                {experience.name} Schedule
              </IgTypography>
            </Grid>
            <Grid item sx={{ marginTop: "-10px" }}>
              <IgTypography sx={{ color: theme.palette.text.secondary }}>
                Timezone:{" "}
                {
                  DateTime.now().setZone(
                    organization?.data?.org?.timezone ?? "UTC"
                  ).offsetNameLong
                }
              </IgTypography>
            </Grid>
          </Grid>
        ) : (
          <Grid container direction={"column"} rowSpacing={4}>
            <Grid item>
              <IgTypography variant={"h2"} sx={{ fontWeight: 600 }}>
                Schedule:{" "}
                <IgTypography
                  variant={"h2"}
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  {experience.name}
                </IgTypography>
              </IgTypography>
            </Grid>
            <Grid item sx={{ marginTop: "-10px" }}>
              <IgTypography sx={{ color: theme.palette.text.secondary }}>
                Timezone:{" "}
                {
                  DateTime.now().setZone(
                    organization?.data?.org?.timezone ?? "UTC"
                  ).offsetNameLong
                }
              </IgTypography>
            </Grid>
            {isUsed.length > 0 && (
              <Grid sx={{ paddingTop: "10px" }}>
                <Alert
                  title="Related Experience Active"
                  severity="warning"
                  sx={{ alignItems: "center", textAlign: "left" }}
                  variant="outlined"
                >
                  <IgTypography
                    sx={{
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    Related Experience Active
                  </IgTypography>
                  <br />
                  <IgTypography sx={{ textAlign: "center" }}>
                    {isUsedInExperimentResult.id
                      ? `The active test "${isUsedInExperimentResult.name}" may overlap with this experience. Check the experiment before activating this experience.`
                      : `Active Experience ${isUsed.length > 1 ? "s" : ""}: ${isUsed.map((used) => `"${used.name}"`).join(", ")} may overlap with this test. Review ${isUsed.length > 1 ? "these Experiences" : "this Experience"}  before scheduling.`}
                  </IgTypography>
                </Alert>
              </Grid>
            )}
          </Grid>
        )
      }
      handleClose={handleClose}
      showDialog={dialog.isOpen}
      minWidth={"475px"}
      dialogHeight={null}
      maxWidth={"sm"}
      handleYesClick={() => {
        handleClose();
      }}
      noButtonText={null}
      shouldNotClose={true}
      rawContent={
        <Grid
          item
          container
          direction={"column"}
          justifyContent={"center"}
          rowSpacing={2}
          flexWrap={"nowrap"}
        >
          <>
            {cancelSchedule ? (
              <Grid item container>
                <IgTypography>
                  Are you sure you want to delete this schedule?
                </IgTypography>
              </Grid>
            ) : (
              <>
                <Grid item>
                  <StartSchedule
                    isLoading={false}
                    startAtSchedule={startAtSchedule}
                    pauseAtSchedule={pauseAtSchedule}
                    endAtSchedule={endAtSchedule}
                    experience={experience}
                    dateTime={startDateTime}
                    setDateTime={setStartDateTime}
                    timeNow={timeNow}
                    isStartSet={startDateTime !== null}
                    action={"start"}
                    currMinDateTime={startDateTime}
                    setErrors={setErrors}
                    disabledReason={getStartDisabledReason(experience)}
                  />
                </Grid>

                <Grid item>
                  <PauseSchedule
                    isLoading={false}
                    action={"pause"}
                    startAtSchedule={startAtSchedule}
                    pauseAtSchedule={pauseAtSchedule}
                    endAtSchedule={endAtSchedule}
                    experience={experience}
                    dateTime={pauseDateTime}
                    setDateTime={setPauseDateTime}
                    isStartSet={startDateTime !== null}
                    timeNow={timeNow}
                    currMinDateTime={startDateTime}
                    setErrors={setErrors}
                    disabledReason={getPausedDisabledReason(
                      experience,
                      startAtSchedule,
                      endAtSchedule,
                      startDateTime,
                      endDateTime
                    )}
                  />
                </Grid>
                {experience?.category === "experiment" && (
                  <Grid item>
                    <EndSchedule
                      action={"end"}
                      isLoading={false}
                      startAtSchedule={startAtSchedule}
                      pauseAtSchedule={pauseAtSchedule}
                      endAtSchedule={endAtSchedule}
                      experience={experience}
                      dateTime={endDateTime}
                      setDateTime={setEndDateTime}
                      isStartSet={startDateTime !== null}
                      timeNow={timeNow}
                      currMinDateTime={startDateTime}
                      setErrors={setErrors}
                      disabledReason={getEndedDisabledReason(
                        experience,
                        startAtSchedule,
                        pauseAtSchedule,
                        startDateTime,
                        pauseDateTime
                      )}
                    />
                  </Grid>
                )}
              </>
            )}
            <Grid
              item
              container
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Grid item xs={6}>
                {!cancelSchedule &&
                (startAtSchedule || pauseAtSchedule || endAtSchedule) ? (
                  <IgButton
                    onClick={() => {
                      setCancelSchedule(true);
                    }}
                    sx={{ fontSize: "13px" }}
                    size={"small"}
                    variant={"text"}
                  >
                    Cancel Schedule
                  </IgButton>
                ) : null}
              </Grid>
              <Grid
                item
                container
                direction={"row"}
                justifyContent={"flex-end"}
                xs={6}
              >
                <IgTooltip
                  title={
                    isConfirmDisabled(errors, experience, startDateTime).reason
                  }
                >
                  <IgButton
                    size={"small"}
                    disabled={
                      !cancelSchedule &&
                      isConfirmDisabled(errors, experience, startDateTime)
                        .isDisabled
                    }
                    variant={"contained"}
                    sx={{ fontSize: "13px" }}
                    onClick={onSaveClick}
                    loading={putTestSchedule.isPending}
                  >
                    {cancelSchedule ? "Yes, I'm Sure" : "Confirm Schedule"}
                  </IgButton>
                </IgTooltip>
              </Grid>
            </Grid>
          </>
        </Grid>
      }
    />
  );
};
